/**
 * BHIMA POS sales API (Stage).
 *
 * Money: totals and settlement amounts are rupees (e.g. 236 / 118), not integer paise.
 * Pass preview settlement_amount unchanged; match to ₹0.01 precision.
 * EVEN amend: settlement_direction "EVEN", settlement_amount 0 (required).
 *   payment_method optional on EVEN — send when cashier changes tender (Cash/UPI/Card).
 *   payment_method required when direction is DUE or REFUND.
 *
 * CASH round-off: nearest ₹1 half-up on bill total only; receipt.round_off + receipt.total (To pay).
 * UPI/CARD: round_off 0, collect exact paise. Print receipt.gst_breakup (from merchandise) — do not recompute.
 * customer_phone: optional on charge/amend (10 digits or E.164). Guest snapshot only.
 *
 * History: GET /store/pos/sales → { sales[], date, scope, next_cursor }
 *   any IST date (default today). Optional from_time / to_time (HH:MM, same day only).
 *   Day summary ignores time and q. Older days OK for reprint; edit → 409 amend_day_locked.
 *   each sale is flat: order_id, display_id, created_at, total, receipt_revision, revised?
 *
 * Open/reprint: GET /store/pos/sales/{order_id} → { receipt } (+ receipt_revision / revised)
 *   (bare receipt also tolerated). Hydrate from receipt (or sale if present).
 *   Stored line_items should still return for reprint even if catalog item later inactive.
 *
 * Amend: POST .../amend → 200 { sale, receipt } — print receipt (same display_id, revision bumped).
 *   WEIGHT/lots: same rules as charge; server credits qty already on bill for stock.
 *   amend_conflict = concurrent amend race (reload); not a client revision field.
 *   Ops and Admin may amend; Customer/Delivery → 403. scope=mine|store for both roles.
 *   Settlement uses payable totals; customer_phone omit keeps, "" clears, value sets.
 *
 * Preview: omit coupon_code when original had one (server keeps it). Sending a different
 * code → 409 coupon_locked. Gone/inactive items may yield item_unavailable / lot_mismatch.
 *
 * Amend errors: 409 amend_day_locked | amount_mismatch | coupon_locked | amend_conflict |
 * insufficient_stock | already_voided; 404 not_found; 400 items_required | invalid_phone.
 * History errors: 400 invalid_time_window | invalid_date.
 *
 * Void (History Delete): POST /store/pos/sales/{order_id}/void — same-day soft void.
 *   Ops/Admin only. Keeps display_id + lines for VOID reprint; restores stock; drops from day summary.
 *   There is no DELETE /store/pos/sales/{id}.
 */
import { apiFetch } from './client';
import { COUPON_VALIDATION_ERRORS } from './promotions';
import { normalizeLot, normalizeProduct } from './inventory';
import { gstBreakup } from '../utils/gst';
import { POS_COUNTER, POS_GSTIN } from '../utils/escpos';
import { normalizeCustomerPhoneForApi, payableForTender } from '../utils/posMoney';
import { lineAmount, quantizeKg, soldBy, stepLabel } from '../utils/soldBy';

function appendCustomerPhone(body, customerPhone, { allowClear = false } = {}) {
  if (customerPhone === undefined) return;
  if (allowClear && customerPhone === '') {
    body.customer_phone = '';
    return;
  }
  const normalized = normalizeCustomerPhoneForApi(customerPhone);
  if (normalized) body.customer_phone = normalized;
}

export const POS_CHARGE_ERRORS = {
  counter_required: 'Enter the counter number.',
  invalid_counter: 'Use 1–16 letters or numbers only for the counter.',
  invalid_date: 'Choose a valid date.',
  items_required: 'Add at least one item to the cart.',
  invalid_payment_method: 'Choose Cash, UPI, Card, or Pay Later.',
  invalid_line: 'One or more items in the cart are invalid.',
  item_unavailable: 'An item is no longer available.',
  invalid_discount_percent: 'Invalid discount percentage.',
  insufficient_stock: 'Not enough kg or packs for one or more items.',
  qty_not_integer: 'Pack item — use whole units.',
  qty_step_mismatch: 'Quantity is not on the allowed step.',
  below_min_order_qty: 'Quantity is below the minimum order.',
  above_max_order_qty: 'Quantity is above the maximum order.',
  invalid_sold_by: 'Sold as must be Pack or Loose.',
  lot_id_required: 'Select a lot for each cart line before charging.',
  lot_mismatch: 'A cart lot is invalid or empty — remove and re-scan the item.',
  id_and_positive_qty_required: 'Enter a valid quantity greater than zero.',
  invalid_phone: 'Enter a valid 10-digit phone number, or leave it blank.',
  customer_name_required: 'Enter the customer’s full name.',
  customer_phone_required: 'Enter a 10-digit phone number.',
  address_required: 'Enter Address Line 1 or select a society (Address Line 2).',
  address_line1_required: 'Enter Address Line 1.',
  address_line2_required: 'Enter or select Address Line 2.',
  invalid_address_line1: 'Address Line 1 is invalid.',
  invalid_address_line2: 'Address Line 2 is invalid.',
  block_required: 'Enter the block (required with society).',
  flat_required: 'Enter the flat (required with society).',
  pay_later_collected: 'Collected Pay Later bills cannot be edited.',
  invalid_time_window: 'Fix from/to clocks.',
  forbidden: 'You do not have permission to run POS sales.',
  not_found: 'Sale not found.',
  amend_day_locked: 'This bill can only be edited on the same day it was charged.',
  amount_mismatch: 'Totals changed — preview again before confirming.',
  coupon_locked: 'This bill’s coupon cannot be changed.',
  amend_conflict: 'Someone else edited this bill — reload and try again.',
  invalid_preview: 'Could not load settlement from preview.',
  already_voided: 'This bill is already voided — refresh the list.',
  already_deleted: 'This bill is already voided — refresh the list.',
  void_day_locked: 'This bill can only be voided on the same day it was charged.',
  delete_day_locked: 'This bill can only be voided on the same day it was charged.',
  void_conflict: 'Someone else changed this bill — reload and try again.',
  invalid_void_reason: 'Void reason must be 200 characters or fewer.',
  ...COUPON_VALIDATION_ERRORS,
};

/** Amend-specific copy (falls back to POS_CHARGE_ERRORS). */
export const POS_AMEND_ERRORS = {
  ...POS_CHARGE_ERRORS,
  items_required: 'Cart cannot be empty — void the bill from History instead.',
  insufficient_stock: 'Not enough stock for the added items (qty already on this bill is credited).',
  not_found: 'Not a POS sale — cannot amend.',
  already_voided: 'This bill is already voided — it cannot be amended.',
};

export const POS_VOID_ERRORS = {
  ...POS_CHARGE_ERRORS,
  already_voided: 'Already deleted — refresh the list.',
  void_day_locked: 'Only same IST calendar day as the original bill.',
  amend_day_locked: 'Only same IST calendar day as the original bill.',
  delete_day_locked: 'Only same IST calendar day as the original bill.',
  void_conflict: 'Concurrent write — reload and try again.',
  insufficient_stock: 'Could not restore stock — reload and try again.',
  not_found: 'Not a POS sale.',
  invalid_void_reason: 'Reason must be 200 characters or fewer.',
  invalid_payment_method: 'Choose Cash, UPI, or Card for the refund.',
  forbidden: 'You do not have permission to void POS sales.',
};

export const POS_INSTANT_ONBOARD_ERRORS = {
  name_required: 'Enter the product name.',
  price_required: 'Enter the selling price or MRP.',
  invalid_price: 'Enter a valid price.',
  selling_above_mrp: 'Selling price cannot be higher than MRP.',
  barcode_required: 'Scan or enter the packaged product barcode.',
  invalid_barcode: 'Barcode must contain digits only.',
  qty_not_integer: 'Pack quantity must be a whole number.',
  invalid_qty: 'Enter a valid bill quantity.',
  invalid_sold_by: 'Choose Pack or Loose / weigh.',
  forbidden: 'You do not have permission to onboard POS items.',
  barcode_taken: 'This barcode already belongs to an existing product.',
  name_taken: 'This product name already exists.',
};

export function mapPosInstantOnboardError(err) {
  const code = err?.code || err?.message;
  return POS_INSTANT_ONBOARD_ERRORS[code]
    || err?.detail
    || err?.message
    || 'Could not add this item.';
}

export function mapPosVoidError(err) {
  const code = err?.code || err?.message;
  return POS_VOID_ERRORS[code]
    || err?.detail
    || err?.message
    || 'Could not void this sale.';
}

/** @deprecated Use mapPosVoidError — History Delete is a same-day void. */
export function mapPosDeleteError(err) {
  return mapPosVoidError(err);
}

export function mapPosChargeError(err, { amend = false } = {}) {
  const code = err?.code || err?.message;
  const body = err?.body || {};
  const catalog = amend ? POS_AMEND_ERRORS : POS_CHARGE_ERRORS;
  if (code === 'below_min_order_qty') {
    const min = body.min_order_qty;
    return min != null ? `Need at least ${min} kg / pcs.` : catalog.below_min_order_qty;
  }
  if (code === 'above_max_order_qty') {
    const max = body.max_order_qty;
    return max != null ? `Cap ${max}.` : catalog.above_max_order_qty;
  }
  if (code === 'qty_step_mismatch') {
    const step = body.qty_step;
    return step != null
      ? `Use steps of ${stepLabel(step) || step}.`
      : catalog.qty_step_mismatch;
  }
  if (code === 'insufficient_stock') {
    if (body.item_id) {
      return amend
        ? `Not enough stock for ${body.item_id} (bill qty is credited).`
        : `Not enough stock for ${body.item_id}.`;
    }
    return catalog.insufficient_stock;
  }
  return catalog[code] || err?.detail || err?.message || (amend
    ? 'Could not amend this sale.'
    : 'Could not confirm this sale.');
}

/** Map preview/amend API errors (day lock, amount mismatch, coupon lock, conflict, …). */
export function mapPosAmendError(err) {
  return mapPosChargeError(err, { amend: true });
}

/**
 * @param {object} payload
 * @param {{ item_id: string, qty: number, lot_id?: string }[]} payload.items
 * @param {'CASH'|'UPI'|'CARD'|'PAY_LATER'} payload.payment_method
 * @param {string} payload.counter
 * @param {string} [payload.coupon_code]
 * @param {number} [payload.discount_percent]
 * @param {string} [payload.customer_phone] 10-digit mobile or E.164 (+91…) — omit when empty
 * @param {string} [payload.customer_name] required when PAY_LATER
 * @param {string} [payload.address_line1] free-text path; optional if address_line2 set
 * @param {string} [payload.address_line2] society path; optional if address_line1 set
 * @param {string} [payload.block] required when PAY_LATER + address_line2
 * @param {string} [payload.flat] required when PAY_LATER + address_line2
 */
export async function createPosSale(payload, token) {
  const body = {
    items: (payload.items || []).map((line) => {
      const item = { item_id: line.item_id, qty: line.qty };
      if (line.lot_id) item.lot_id = line.lot_id;
      return item;
    }),
    payment_method: payload.payment_method,
    counter: payload.counter,
    discount_percent: payload.discount_percent ?? 0,
  };
  if (payload.coupon_code) body.coupon_code = payload.coupon_code;
  appendCustomerPhone(body, payload.customer_phone);
  if (payload.customer_name != null && String(payload.customer_name).trim()) {
    body.customer_name = String(payload.customer_name).trim().slice(0, 80);
  }
  if (payload.address_line1 != null && String(payload.address_line1).trim()) {
    body.address_line1 = String(payload.address_line1).trim();
  }
  if (payload.address_line2 != null && String(payload.address_line2).trim()) {
    body.address_line2 = String(payload.address_line2).trim();
  }
  if (payload.block != null && String(payload.block).trim()) {
    body.block = String(payload.block).trim();
  }
  if (payload.flat != null && String(payload.flat).trim()) {
    body.flat = String(payload.flat).trim();
  }
  return apiFetch('/store/pos/sales', { method: 'POST', token, body });
}

/** Reprint / open — `{id}` must be `order_id`, not `display_id`.
 * Typically `{ receipt }` (+ receipt_revision / revised); bare receipt also OK.
 */
export async function getPosSale(orderId, token) {
  return apiFetch(`/store/pos/sales/${encodeURIComponent(orderId)}`, { token });
}

/** Void a POS sale (History Delete) — POST /store/pos/sales/{order_id}/void. */
export async function voidPosSale(orderId, payload = {}, token) {
  const body = {};
  const reason = String(payload.reason ?? payload.void_reason ?? '').trim();
  if (reason) body.reason = reason.slice(0, 200);
  if (payload.payment_method) body.payment_method = payload.payment_method;
  return apiFetch(`/store/pos/sales/${encodeURIComponent(orderId)}/void`, {
    method: 'POST',
    token,
    body,
  });
}

/** @deprecated Use voidPosSale — there is no DELETE /store/pos/sales/{id}. */
export async function deletePosSale(orderId, token) {
  return voidPosSale(orderId, {}, token);
}

/**
 * Normalize GET sale / history open payload for reprint or edit hydrate.
 * Prefers receipt; fills gaps from sale / list row. Totals are rupees.
 */
export function normalizePosSalePayload(data, fallback = {}) {
  const receipt = data?.receipt && typeof data.receipt === 'object' ? data.receipt : null;
  const sale = data?.sale && typeof data.sale === 'object' ? data.sale : null;
  const bare = !receipt && !sale && data && typeof data === 'object' ? data : null;
  const primary = receipt || bare || sale || {};
  const secondary = sale && receipt ? sale : {};

  const orderId = primary.order_id
    || secondary.order_id
    || fallback.order_id
    || fallback.orderId
    || fallback.id
    || null;
  const revision = primary.receipt_revision
    ?? secondary.receipt_revision
    ?? fallback.receipt_revision
    ?? null;
  const voided = primary.voided === true
    || secondary.voided === true
    || fallback.voided === true
    || String(primary.status || secondary.status || fallback.status || '').toUpperCase() === 'VOIDED'
    || String(primary.payment_status || secondary.payment_status || fallback.payment_status || '').toUpperCase() === 'VOIDED';
  const revised = !voided && (
    primary.revised === true
    || secondary.revised === true
    || fallback.revised === true
    || (revision != null && Number(revision) > 1)
  );

  return {
    ...secondary,
    ...primary,
    order_id: orderId,
    display_id: primary.display_id || secondary.display_id || fallback.display_id || orderId,
    line_items: primary.line_items || secondary.line_items || [],
    coupon_code: primary.coupon_code ?? secondary.coupon_code ?? fallback.coupon_code ?? null,
    discount_amount: primary.discount_amount ?? secondary.discount_amount ?? fallback.discount_amount ?? 0,
    discount_percent: primary.discount_percent ?? secondary.discount_percent ?? fallback.discount_percent ?? null,
    total: primary.total ?? secondary.total ?? fallback.total,
    receipt_revision: revision,
    revised,
    voided,
    void_reason: primary.void_reason
      ?? secondary.void_reason
      ?? fallback.void_reason
      ?? null,
    status: primary.status || secondary.status || fallback.status || null,
    payment_status: primary.payment_status || secondary.payment_status || fallback.payment_status || null,
    created_at: primary.created_at
      || secondary.created_at
      || fallback.created_at
      || fallback.createdAt
      || fallback.charged_at,
  };
}

/** IST calendar date as YYYY-MM-DD (POS history default). */
export function posSalesDateIst(date = new Date()) {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Kolkata',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(date);
}

/**
 * POS sales history — GET /store/pos/sales
 * @param {{
 *   date?: string,
 *   scope?: 'mine'|'store',
 *   from_time?: string,
 *   to_time?: string,
 *   q?: string,
 *   limit?: number,
 *   cursor?: string,
 * }} params
 * @param {string} token
 * @returns {Promise<{
 *   sales: object[],
 *   date?: string,
 *   scope?: string,
 *   summary?: {
 *     bill_count: number,
 *     gross_total: number,
 *     by_payment_method: Record<string, { count: number, amount: number }>,
 *     truncated: boolean,
 *   },
 *   next_cursor?: string|null,
 * }>}
 */
export async function listPosSales(params = {}, token) {
  const usp = new URLSearchParams();
  if (params.date) usp.set('date', params.date);
  if (params.scope) usp.set('scope', params.scope);
  if (params.from_time) usp.set('from_time', params.from_time);
  if (params.to_time) usp.set('to_time', params.to_time);
  if (params.q) usp.set('q', params.q);
  if (params.limit != null) usp.set('limit', String(params.limit));
  if (params.cursor) usp.set('cursor', params.cursor);
  const qs = usp.toString();
  return apiFetch(`/store/pos/sales${qs ? `?${qs}` : ''}`, { token });
}

/**
 * Create a POS-only incomplete catalog stub and seed its billed quantity.
 * Prices are used only here; the later charge sends item_id, qty and lot_id.
 */
export async function instantOnboardPos(payload, token) {
  const soldByValue = String(payload.sold_by || '').toUpperCase();
  const body = {
    sold_by: soldByValue,
    name: String(payload.name || '').trim(),
    qty: Number(payload.qty),
  };
  if (soldByValue === 'EACH') body.barcode = String(payload.barcode || '').trim();
  if (payload.selling_price !== '' && payload.selling_price != null) {
    body.selling_price = Number(payload.selling_price);
  }
  if (payload.mrp !== '' && payload.mrp != null) body.mrp = Number(payload.mrp);
  if (payload.image_url) body.image_url = String(payload.image_url).trim();

  const raw = await apiFetch('/store/pos/instant-onboard', {
    method: 'POST',
    token,
    body,
  });
  const item = normalizeProduct(raw?.item || {});
  const lots = Array.isArray(raw?.lots) ? raw.lots.map(normalizeLot).filter(Boolean) : [];
  return {
    item,
    lotId: raw?.lot_id || lots[0]?.lotId || null,
    lots,
    lotPickRequired: Boolean(raw?.lot_pick_required),
    priceMin: raw?.price_min ?? null,
    priceMax: raw?.price_max ?? null,
  };
}

function mapPosCartItems(items = []) {
  return items.map((line) => {
    const item = { item_id: line.item_id, qty: line.qty };
    if (line.lot_id) item.lot_id = line.lot_id;
    return item;
  });
}

/**
 * Preview an amend — POST /store/pos/sales/{id}/preview (no stock write).
 * Settlement money is rupees (not integer paise). Omit coupon_code when original had one.
 * customer_phone: omit keeps, "" clears, value sets.
 */
export async function previewPosAmend(orderId, payload, token) {
  const body = {
    items: mapPosCartItems(payload.items),
    discount_percent: payload.discount_percent ?? 0,
  };
  if (payload.coupon_code) body.coupon_code = payload.coupon_code;
  if (Object.prototype.hasOwnProperty.call(payload, 'customer_phone')) {
    appendCustomerPhone(body, payload.customer_phone, { allowClear: true });
  }
  return apiFetch(`/store/pos/sales/${encodeURIComponent(orderId)}/preview`, {
    method: 'POST',
    token,
    body,
  });
}

/**
 * Confirm amend — POST /store/pos/sales/{id}/amend → { sale, receipt } (print receipt).
 * settlement_amount is rupees and must match preview exactly (payable totals after round-off).
 * payment_method required when direction is DUE or REFUND (CASH|UPI|CARD).
 * Optional on EVEN to update the bill's tender when the customer switched method.
 * customer_phone: omit keeps, "" clears, value sets.
 * @param {string} orderId
 * @param {{
 *   items: { item_id: string, qty: number, lot_id?: string }[],
 *   settlement_direction: 'DUE'|'REFUND'|'EVEN',
 *   settlement_amount: number,
 *   payment_method?: 'CASH'|'UPI'|'CARD',
 *   discount_percent?: number,
 *   customer_phone?: string,
 * }} payload
 * @param {string} token
 * @returns {Promise<{ sale?: object, receipt?: object }>}
 */
export async function amendPosSale(orderId, payload, token) {
  const body = {
    items: mapPosCartItems(payload.items),
    settlement_direction: payload.settlement_direction,
    settlement_amount: payload.settlement_amount,
  };
  if (payload.payment_method) body.payment_method = payload.payment_method;
  if (payload.discount_percent != null) body.discount_percent = payload.discount_percent;
  if (Object.prototype.hasOwnProperty.call(payload, 'customer_phone')) {
    appendCustomerPhone(body, payload.customer_phone, { allowClear: true });
  }
  return apiFetch(`/store/pos/sales/${encodeURIComponent(orderId)}/amend`, {
    method: 'POST',
    token,
    body,
  });
}

/** Local receipt preview before the sale is confirmed on the server. */
export function buildPosPreviewReceipt({
  cart,
  subtotal,
  discount = 0,
  discountPercent,
  total,
  paymentMethod,
  couponCode,
  cashierName,
  customerNumber,
  customerPhone,
}) {
  const merchandise = total ?? Math.max(0, (subtotal || 0) - (discount || 0));
  const { payable, round_off } = payableForTender(merchandise, paymentMethod);
  return {
    display_id: 'Pending',
    order_id: null,
    created_at: new Date().toISOString(),
    counter: POS_COUNTER,
    gstin: POS_GSTIN,
    cashier_name: cashierName || null,
    customer_number: customerNumber || null,
    customer_phone: customerPhone || null,
    line_items: cart.map((line) => ({
      item_id: line.id,
      sku: line.sku,
      name: line.name,
      qty: soldBy(line) === 'WEIGHT' ? quantizeKg(line.qty) : line.qty,
      unit: line.unit,
      sold_by: soldBy(line),
      unit_price: line.price,
      line_total: lineAmount(line.price, line.qty),
    })),
    subtotal,
    discount_amount: discount,
    discount_percent: discountPercent ?? (subtotal > 0 && discount > 0 ? (discount / subtotal) * 100 : null),
    coupon_code: couponCode || null,
    round_off,
    total: payable,
    payment_method: paymentMethod,
    gst_breakup: gstBreakup(cart, discount, subtotal),
    note: '(Inclusive of GST)',
  };
}
